import { BIOME } from "@eldrun/worldgen";
import { stride } from "../audio/audio-model.ts";

/**
 * Chão de um passo, para o efeito: `agua` nadando; `raso` com os pés na água (andando no raso); `molhada`
 * a areia úmida da beira; `madeira` sobre construção (sem efeito); o resto pelo bioma.
 */
export type StepSurface = "agua" | "raso" | "lama" | "molhada" | "grama" | "areia" | "terra" | "pedra" | "madeira";

/** Pés tantos m abaixo do mar: andando no raso. */
export const WADE_DEPTH = 0.08;
/** A areia molhada vai até tantos m acima do mar (a faixa úmida fixa do terreno, `terrain.ts`). */
export const WET_SAND = 0.35;

/**
 * O chão do passo em `y` (os pés): nadando, água; abaixo do mar, raso; sobre construção, madeira; senão o
 * bioma (`grassy`: o capim está à vista ali, nas Opções e sem construção por cima).
 */
export function stepSurface(biome: number, y: number, seaLevel: number, onBox: boolean, swimming: boolean, grassy: boolean): StepSurface {
  if (swimming) return "agua";
  if (onBox) return "madeira";
  if (y < seaLevel - WADE_DEPTH) return "raso";
  switch (biome) {
    case BIOME.praia:
      return y < seaLevel + WET_SAND ? "molhada" : "areia";
    case BIOME.mangue:
      return "lama";
    case BIOME.mata:
    case BIOME.cerrado:
      return grassy ? "grama" : "terra";
    case BIOME.serra:
      return "pedra";
    default:
      return "raso";
  }
}

export interface StepBody {
  x: number;
  /** Os pés. */
  y: number;
  z: number;
  /** Velocidade no plano (m/s). */
  speed: number;
  grounded: boolean;
  swimming: boolean;
}

export interface StepEvent {
  surface: StepSurface;
  /** 1 andando, mais correndo; 2 ao entrar na água. */
  strength: number;
  x: number;
  y: number;
  z: number;
}

interface Track {
  x: number;
  z: number;
  acc: number;
  wet: boolean;
}

/**
 * Passos de cada corpo para os efeitos de chão: a cada passada andada no chão (ou nadando) um passo, com a
 * mesma passada dos sons (`stride`), então o respingo sai junto do som; entrar na água (os pés passam do
 * nível do mar, ou começa a nadar) é um passo forte. O chão só é classificado quando há passo
 * (`surfaceOf`). O primeiro quadro de um corpo só guarda o estado; um salto de mais de 3 m (teleporte,
 * renascer) zera a passada.
 */
export class FootSteps {
  private readonly tracks = new Map<string, Track>();

  frame(id: string, b: StepBody, seaLevel: number, surfaceOf: (b: StepBody) => StepSurface): StepEvent | undefined {
    const wet = b.swimming || b.y < seaLevel - WADE_DEPTH;
    const t = this.tracks.get(id);
    if (!t) {
      this.tracks.set(id, { x: b.x, z: b.z, acc: 0, wet });
      return undefined;
    }
    const moved = Math.hypot(b.x - t.x, b.z - t.z);
    const entered = wet && !t.wet && moved < 3;
    t.x = b.x;
    t.z = b.z;
    t.wet = wet;
    if (entered) {
      t.acc = 0;
      return { surface: b.swimming ? "agua" : "raso", strength: 2, x: b.x, y: b.y, z: b.z };
    }
    if (moved >= 3) {
      t.acc = 0;
      return undefined;
    }
    if (b.speed <= 0.5 || !(b.grounded || b.swimming)) return undefined;
    t.acc += moved;
    if (t.acc < stride(b.speed)) return undefined;
    t.acc = 0;
    return { surface: surfaceOf(b), strength: b.speed > 5 ? 1.3 : 1, x: b.x, y: b.y, z: b.z };
  }

  remove(id: string): void {
    this.tracks.delete(id);
  }

  clear(): void {
    this.tracks.clear();
  }
}
